import React from "react";
import { View, TouchableOpacity } from "~/ui/primitives";
import Animated, { useSharedValue, withTiming, useAnimatedProps, useAnimatedStyle } from "~/ui/reanimated";
import { useTheme } from '~/ui/styled'
import Svg, { Circle } from "~/ui/svg";

const AnimatedCircle = Animated.createAnimatedComponent(Circle);
const AnimatedTouchable = Animated.createAnimatedComponent(TouchableOpacity);
const AnimatedSvg = Animated.createAnimatedComponent(Svg);

interface RecordingButtonProps {
  size?: number;
  thickness?: number;
  onTakePhoto: () => void;
  onStartRecord: () => void;
  onStopRecord: () => void;
  maxDuration?: number;
}

const RecordingButton: React.FC<RecordingButtonProps> = ({
  size = 60,
  thickness = 4,
  onTakePhoto,
  onStartRecord,
  onStopRecord,
  maxDuration = 15000,
}) => {
  const theme = useTheme();
  const recording = useSharedValue(false);
  const progress = useSharedValue(0);
  const scale = useSharedValue(1);

  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  const animatedProps = useAnimatedProps(() => ({
    strokeDashoffset: circumference * (1 - progress.value),
  }));

  const animatedScaleStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const animatePressIn = () => {
    scale.value = withTiming(1.5, { duration: 150 });
  };

  const animatePressOut = () => {
    scale.value = withTiming(1.0, { duration: 150 });
  };

  const handlePress = () => {
    if (!recording.value) onTakePhoto?.();
  };

  const handleLongPress = () => {
    if (recording.value) return;
    recording.value = true;
    progress.value = 0;

    progress.value = withTiming(1, { duration: maxDuration });

    onStartRecord?.();
  };

  const handleRelease = () => {
    if (!recording.value) return;
    progress.value = 0;
    recording.value = false;
    onStopRecord?.();
  };

  return (
    <AnimatedTouchable
      activeOpacity={1}
      delayLongPress={200}
      onClick={handlePress}
      onLongPress={handleLongPress}
      onPressIn={animatePressIn}
      onPressOut={() => {
        animatePressOut();
        handleRelease();
      }}
      style={[
        { justifyContent: "center", alignItems: "center" },
        animatedScaleStyle,
      ]}
    >
      <View style={{ height: size, width: size, justifyContent: "center", alignItems: "center" }}>
        <AnimatedSvg width={size} height={size} style={animatedScaleStyle}>
          {/* 背景圈 */}
          <Circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={theme.colors.background}
            strokeWidth={thickness}
            fill="none"
          />

          {/* 动画进度圈 */}
          <AnimatedCircle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={theme.colors.primary}
            strokeWidth={thickness}
            fill="none"
            strokeDasharray={`${circumference}, ${circumference}`}
            strokeLinecap="round"
            animatedProps={animatedProps}
            rotation="-90"
            originX={size / 2}
            originY={size / 2}
          />
        </AnimatedSvg>

        {/* 中心按钮 */}
        <Animated.View
          style={{
            position: "absolute",
            height: 44,
            width: 44,
            backgroundColor: recording.value ? theme.colors.primary : theme.colors.background,
            borderRadius: 22,
          }}
        />
      </View>
    </AnimatedTouchable>
  );
};


export default RecordingButton;
